// Display and receipt validation only. Eligibility and capacity are checked by the backend.
const invalidList = "Die Kursliste konnte nicht zuverlässig geladen werden. Bitte aktualisiere sie.";
const uncertainBooking = "Die Reservierung konnte nicht bestätigt werden. Bitte aktualisiere die Kursliste, bevor du es erneut versuchst.";

export function readMoreDanceEnvelope(raw) {
  let value = raw;
  for (let depth = 0; depth < 2 && typeof value === "string"; depth += 1) {
    try { value = JSON.parse(value); } catch { throw new Error(invalidList); }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error(invalidList);
  return value;
}

export function moreDanceErrorMessage(raw, fallback = invalidList) {
  try {
    const value = readMoreDanceEnvelope(raw);
    if (typeof value.message === "string" && value.message.trim()) {
      return value.message.replace(/[\u0000-\u001f\u007f]/g, " ").trim().slice(0, 600);
    }
  } catch { /* An invalid response must never appear as a booking confirmation. */ }
  return fallback;
}

function positiveId(value) {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

function requiredText(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function formatMoreDanceDate(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return "";
  const date = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) return "";
  return `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}`;
}

export function formatMoreDanceWeekday(value) {
  const names = {
    mo: "Montag", mon: "Montag", monday: "Montag", montag: "Montag",
    di: "Dienstag", tue: "Dienstag", tuesday: "Dienstag", dienstag: "Dienstag",
    mi: "Mittwoch", wed: "Mittwoch", wednesday: "Mittwoch", mittwoch: "Mittwoch",
    do: "Donnerstag", thu: "Donnerstag", thursday: "Donnerstag", donnerstag: "Donnerstag",
    fr: "Freitag", fri: "Freitag", friday: "Freitag", freitag: "Freitag",
    sa: "Samstag", sat: "Samstag", saturday: "Samstag", samstag: "Samstag",
    so: "Sonntag", sun: "Sonntag", sunday: "Sonntag", sonntag: "Sonntag",
  };
  return names[String(value || "").toLowerCase().trim()] || requiredText(value) || "";
}

function displayTime(value) {
  if (typeof value !== "string" || !/^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/.test(value)) return null;
  return value.slice(0, 5);
}

export function parseMoreDanceList(raw) {
  const payload = readMoreDanceEnvelope(raw);
  if (payload.status !== true) throw new Error(moreDanceErrorMessage(payload));
  if (!Array.isArray(payload.result)) throw new Error(invalidList);
  const ids = new Set();
  const courses = payload.result.map((row) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) throw new Error(invalidList);
    const id = positiveId(row.id);
    const availablePlaces = Number(row.available_places);
    // The server label includes its category (for example "Anfänger 1").
    // A client-side level comparison would incorrectly merge different categories.
    const level = ["string", "number"].includes(typeof row.level) &&
      (typeof row.level !== "number" || Number.isFinite(row.level)) ? requiredText(String(row.level)) : null;
    const levelLabel = level && /^\d+(?:\.\d+)?$/.test(level) ? `Level ${level}` : level;
    const title = requiredText(row.title);
    const style = requiredText(row.style);
    const weekday = formatMoreDanceWeekday(row.weekday);
    const time = displayTime(row.time);
    const endTime = row.end_time == null || row.end_time === "" ? "" : displayTime(row.end_time);
    const reserved = row.reserved === true;
    if (!id || ids.has(id) || !title || !style || !weekday || !time || endTime === null ||
        !level || !["male", "female"].includes(row.role) ||
        typeof row.reserved !== "boolean" || !Number.isSafeInteger(availablePlaces) ||
        availablePlaces < 0 || (!reserved && availablePlaces === 0)) throw new Error(invalidList);
    if (row.start_date != null && row.start_date !== "" && !formatMoreDanceDate(row.start_date)) throw new Error(invalidList);
    ids.add(id);
    return { id, title, style, weekday, time, endTime, level, levelLabel, role: row.role,
      availablePlaces, reserved, startDate: row.start_date || "" };
  });
  return { courses, message: typeof payload.message === "string" ? payload.message.trim() : "" };
}

export function parseMoreDanceReservation(raw, courseId) {
  const payload = readMoreDanceEnvelope(raw);
  if (payload.status !== true) throw new Error(moreDanceErrorMessage(payload, uncertainBooking));
  if (!positiveId(courseId) || !["more_dance_reserved", "more_dance_already_reserved"].includes(payload.code) ||
      payload.reserved !== true || positiveId(payload.course_id) !== positiveId(courseId) ||
      payload.main_course_unchanged !== true) throw new Error(uncertainBooking);
  if (payload.start_date != null && payload.start_date !== "" && !formatMoreDanceDate(payload.start_date)) throw new Error(uncertainBooking);
  return { alreadyReserved: payload.code === "more_dance_already_reserved", startDate: payload.start_date || "" };
}

export function moreDanceButtonState(course, pendingId, loading, refreshRequired) {
  if (course.reserved) return { disabled: true, text: "Bereits reserviert" };
  if (pendingId === course.id) return { disabled: true, text: "Wird reserviert …" };
  return { disabled: Boolean(pendingId || loading || refreshRequired), text: "Fixen Platz reservieren" };
}

export function buildMoreDanceUrl(base, session, action) {
  if (!["get_more_dance_courses", "reserve_more_dance_course"].includes(action)) throw new Error("Unbekannter Vorgang.");
  if (!positiveId(session?.userId) || !session?.cookie || !session?.userType || !positiveId(session?.schoolId)) {
    throw new Error("Bitte melde dich erneut an, um passende Kurse zu sehen.");
  }
  let url;
  try { url = new URL(base); } catch { throw new Error(invalidList); }
  url.searchParams.set("login_cookie", session.cookie);
  url.searchParams.set("login_user_id", String(session.userId));
  url.searchParams.set("login_action_type", session.userType);
  url.searchParams.set("school_id", String(session.schoolId));
  url.searchParams.set("user_id", String(session.userId));
  url.searchParams.set("status", "1");
  url.searchParams.set("f", action);
  return url.toString();
}
